import { DisplayMode } from "../types";
import { apiSlice } from "./api";

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string | undefined;
  url: string; // where to go after creating user
  recaptchaToken: string;
}
export interface CreateUserResponse {}

export interface LinkUserToGoogleRequest {
  googleUserIdToken: string;
}
export interface LinkUserToGoogleResponse {
  isLinked: boolean;
}

export interface SendLoginEmailRequest {
  email: string;
  url: string; // where to go logging in
  recaptchaToken: string;
}
export interface SendLoginEmailResponse {}

export interface GetUserAttributesRequest {
  email?: string; // email is optional. If not provided, then uses the current user.
  userId?: string; // This is the document ID for the user in the users collection.
  // userId is optional. If not provided, then uses the current user.
  // In order to use email or userId, the user must be an admin.
  // If both are provided, then email takes precedence.
  // If neither is provided, then uses the current user.
}
export interface GetUserAttributesResponse {
  updateTime: string; // ISO 8601
  firstName: string;
  lastName?: string;
  displayMode: DisplayMode;
  isUnsubscribedAll: boolean;
  unsubscribedHosts: string[]; // email addresses
  canOwnVents: boolean;
  emailAllotment: number;
  ventLimit: number;
  guestLimit: number;
  isAdmin: boolean;
  email: string; // email address of the user
  userId: string; // user's ID from user doc in Firestore
  isDisabled: boolean; // true if the user is disabled in Firebase
  smsNumber: string; // SMS number of the user
  canTestTextChannel: boolean; // true if the user can test the text channel
  isOptedOutTextChannel: boolean; // true if the user has opted out of the text channel
}

export interface SaveUserConfigAttributesRequest {
  firstName: string;
  lastName?: string;
  displayMode: DisplayMode;
  isUnsubscribedAll: boolean;
  unsubscribedHosts: string[]; // email addresses
  smsNumber: string; // SMS number of the user
  isOptedOutTextChannel: boolean; // true if the user has opted out of the text channel
  canTestTextChannel: boolean; // true if the user can test the text channel
  updateTime: string; // ISO 8601
}
export interface SaveUserConfigAttributesResponse {
  updateTime: string; // ISO 8601
  userId: string; // user's ID from user doc in Firestore
}

export interface SaveUserRequest {
  userId: string; // user's UID from authentication
  firstName: string;
  lastName?: string;
  canOwnVents: boolean;
  emailAllotment: number;
  ventLimit: number;
  guestLimit: number;
  isAdmin: boolean;
  isDisabled: boolean; // true if the user is disabled in Firebase
  canTestTextChannel: boolean;
  isOptedOutTextChannel: boolean;
  updateTime: string; // ISO 8601
}
export interface SaveUserResponse {
  updateTime: string; // ISO 8601
}

export const usersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserAttributes: builder.query<
      GetUserAttributesResponse,
      GetUserAttributesRequest
    >({
      query: (body) => ({
        url: "getUserAttributes",
        body: JSON.stringify(body),
      }),
    }),

    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      query: (body) => ({
        url: "createUser",
        body: JSON.stringify(body),
      }),
    }),

    sendLoginEmail: builder.mutation<
      SendLoginEmailResponse,
      SendLoginEmailRequest
    >({
      query: (body) => ({
        url: "sendLoginEmail",
        body: JSON.stringify(body),
      }),
    }),

    saveUserConfigAttributes: builder.mutation<
      SaveUserConfigAttributesResponse,
      SaveUserConfigAttributesRequest
    >({
      query: (body) => {
        return {
          url: "saveUserConfigAttributes",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData("getUserAttributes", {}, (draft) => {
              draft.firstName = request.firstName;
              draft.lastName = request.lastName;
              draft.displayMode = request.displayMode;
              draft.updateTime = data.updateTime;
              draft.isUnsubscribedAll = request.isUnsubscribedAll;
              draft.unsubscribedHosts = request.unsubscribedHosts;
              if (request.smsNumber !== draft.smsNumber) {
                draft.isOptedOutTextChannel = false;
              }
              draft.smsNumber = request.smsNumber;
            })
          );
        } catch {}
      },
    }),

    saveUser: builder.mutation<SaveUserResponse, SaveUserRequest>({
      query: (body) => {
        return {
          url: "saveUser",
          body: JSON.stringify(body),
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData(
              "getUserAttributes",
              { userId: request.userId },
              (draft) => {
                draft.firstName = request.firstName;
                draft.lastName = request.lastName;
                draft.canOwnVents = request.canOwnVents;
                draft.emailAllotment = request.emailAllotment;
                draft.ventLimit = request.ventLimit;
                draft.guestLimit = request.guestLimit;
                draft.isAdmin = request.isAdmin;
                draft.isDisabled = request.isDisabled;
                draft.canTestTextChannel = request.canTestTextChannel;
                draft.isOptedOutTextChannel = request.isOptedOutTextChannel;
                draft.updateTime = data.updateTime;
              }
            )
          );
        } catch {}
      },
    }),

    //
  }),
});

export const {
  useCreateUserMutation,
  useGetUserAttributesQuery,
  useLazyGetUserAttributesQuery,
  useSaveUserConfigAttributesMutation,
  useSendLoginEmailMutation,
  useSaveUserMutation,
} = usersApi;
